/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { GuideProgram } from 'client-code/filtered-guide';
import { PreviewType } from 'client-code/previews';

import { STAR_RATING_MAX_STARS } from '../../../../config';
import { DeviceModel } from '../../../../core/device';
import { BrandingUiInfoKeys, Channel } from '../../../../core/entities';
import { ServicesClientImpl } from '../../../../core/interactors';
import { Locale, LocaleKeys } from '../../../../core/locale';
import { TOfferPreview } from '../../../../core/types';
import { RecordingRestriction } from '../../../../core/usecases';
import { DateTime } from '../../../../core/utils';
import { RestrictionMessage } from '../../../common';
import { OfferPreviewFormatter } from '../../../common/OfferPreviewFormatter';
import { ImageLoader } from '../../../ImageLoader';
import { ImageLoaderTypes } from '../../../ImageLoader/ImageLoaderTypes';
import { RecordingStatusIcon } from '../../RecordingStatusIcon';
import {
    calculateMaskSizeClass,
    getPreviewDescriptionAttributes,
    getReasonWhyChannelIsFilteredOut,
    isChannelPartOfFilteredOutChannels,
} from '../filteredguide.utils';

type FilteredGuidePreviewProgramProps = {
    selectedChannel: Channel;
    offerPreviewInfo: TOfferPreview;
    selectedGuideProgram: GuideProgram;
    hideMetadata: boolean;
    recordingsStatusMap: Map<string, string>;
};

const FilteredGuidePreviewProgram = ({
    selectedChannel,
    offerPreviewInfo,
    selectedGuideProgram,
    hideMetadata,
    recordingsStatusMap,
}: FilteredGuidePreviewProgramProps) => {
    const [shouldTrimDescription, setShouldTrimDescription] = useState(false);
    const services = new ServicesClientImpl();

    const descriptionRef = useRef<HTMLDivElement>(null);
    const { offerPreview } = offerPreviewInfo;
    const { contentPreview } = offerPreview;
    const formatter = new OfferPreviewFormatter(offerPreview, hideMetadata);

    const description =
        formatter.getPreviewDescription() || Locale.getString(LocaleKeys.DESCRIPTION_NOT_AVAILABLE);
    const title = formatter.getPreviewTitle();
    const isMovie = contentPreview?.previewType === PreviewType.MoviePreview;
    const movieYear = isMovie ? contentPreview?.movieYear : null;
    const showNewIcon = contentPreview?.isNew;
    const starRating = formatter.getStarRating();
    const newIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON);
    const liveIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.LIVE_ICON);

    const isChannelFilteredOut = isChannelPartOfFilteredOutChannels(selectedChannel);

    const { attributes, attributesClass } = useMemo(() => {
        const attr = getPreviewDescriptionAttributes(
            contentPreview?.channel?.videoResolution ?? contentPreview?.videoResolution,
            contentPreview?.hasCc,
            contentPreview?.isThreeD,
            contentPreview?.hasSap,
        );

        const attrClassname = calculateMaskSizeClass(attr.join(', '));

        return { attributes: attr, attributesClass: attrClassname };
    }, [contentPreview]);

    useLayoutEffect(() => {
        if (!descriptionRef.current || description.length === 0) return;

        let attempts = 0;
        const maxAttempts = 10;

        const checkHeight = () => {
            const contentHeight = descriptionRef.current?.clientHeight || 0;

            if (contentHeight > 0) {
                setShouldTrimDescription(contentHeight > 144);
            } else if (attempts < maxAttempts) {
                attempts += 1;
                requestAnimationFrame(checkHeight);
            }
        };

        checkHeight();
    }, [description]);

    const recordingStatus = recordingsStatusMap.get(
        `${selectedChannel?.stationId}${selectedGuideProgram.offerId}`,
    );

    return (
        <div className="filtered-guide-preview-program">
            <div>
                <div className="content-airing-info program-preview-channel-airing">
                    <span>
                        {DateTime.createDayDateTimeRange({
                            startTime: selectedGuideProgram.startTime * 1000,
                            endTime: selectedGuideProgram.endTime * 1000,
                            lang: DeviceModel.getTVProperties().getLang(),
                        })}
                    </span>
                </div>
                <div className="content-airing-info program-preview-channel-info">
                    <span>{selectedChannel.channelNumber}</span>
                    <div
                        className={`preview-stream-type-icon ${selectedChannel.videoResolution}`}
                    ></div>
                    <span>{selectedChannel.callSign}</span>
                </div>

                <div className="content-airing-info program-preview-top">
                    <span className="title-text">{title}</span>
                    {movieYear && !hideMetadata && (
                        <span className="movie-year"> ({movieYear})</span>
                    )}
                    {showNewIcon && newIcon?.imageUrl && (
                        <img
                            src={newIcon.imageUrl}
                            className="icon-new"
                            data-testid="filtered-guide-title-preview-new-icon"
                        />
                    )}
                </div>

                <div
                    className={`content-airing-info program-description ${shouldTrimDescription ? 'trimmed' : ''}`}
                    ref={descriptionRef}
                >
                    <div className={`description-wrapper ${attributesClass}`}>{description}</div>
                    {!hideMetadata && (
                        <span className="show-attributes">{`(${attributes.join(', ')})`} </span>
                    )}
                </div>

                {!hideMetadata && (
                    <>
                        <div className="content-airing-info program-rating-genre">
                            {formatter.getInternalRatings().map((rating, index) => (
                                <span className="internal-ratings" key={index}>
                                    <span className="rating-text">{rating}</span>
                                </span>
                            ))}
                            <span>{formatter.getGenres().split(',').splice(0, 2).join(',')}</span>
                        </div>
                        <div className="content-airing-info program-duration-rating">
                            <span>{formatter.getDurationTime()}</span>
                            {isMovie && formatter.hasStarRating() && (
                                <>
                                    <span className="rating-separator">|</span>
                                    <span className="star-rating">
                                        {new Array(Math.floor(starRating))
                                            .fill(0)
                                            .map((_, index) => (
                                                <span
                                                    className="star-icon full"
                                                    key={`full-${index}`}
                                                />
                                            ))}
                                        {starRating > Math.floor(starRating) && (
                                            <span className="star-icon half" />
                                        )}
                                        {new Array(STAR_RATING_MAX_STARS - Math.ceil(starRating))
                                            .fill(0)
                                            .map((_, index) => (
                                                <span
                                                    className="star-icon empty"
                                                    key={`empty-${index}`}
                                                />
                                            ))}
                                    </span>
                                </>
                            )}
                        </div>
                        <div className="content-airing-info program-cast">
                            <span>{formatter.getParsedCast()}</span>
                        </div>
                    </>
                )}

                <RestrictionMessage
                    displayType={RecordingRestriction.PREVIEW_AREA}
                    channel={selectedChannel}
                />

                {isChannelFilteredOut && !hideMetadata && (
                    <div className="content-airing-info channel-filtered-out">
                        <span>{getReasonWhyChannelIsFilteredOut(selectedChannel)}</span>
                    </div>
                )}
            </div>

            {!hideMetadata && (
                <div className="content-airing-info program-preview-bottom">
                    <span className="decorative-icons">
                        {liveIcon?.imageUrl && contentPreview?.isLive && (
                            <ImageLoader
                                src={liveIcon.imageUrl}
                                imageType={ImageLoaderTypes.PROVIDER_IMAGE_PREVIEW_ICON}
                            />
                        )}
                        {formatter
                            .getProvidersImageUrls()
                            ?.map((url: string, index: number) => (
                                <ImageLoader
                                    key={index}
                                    src={url}
                                    imageType={ImageLoaderTypes.PROVIDER_IMAGE_PREVIEW_ICON}
                                />
                            ))}
                        {recordingStatus && (
                            <RecordingStatusIcon
                                type={recordingStatus}
                                styleClass={ImageLoaderTypes.PROVIDER_IMAGE_PREVIEW_ICON}
                            />
                        )}
                    </span>
                </div>
            )}
        </div>
    );
};

export default FilteredGuidePreviewProgram;
